import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeftIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ClockIcon,
  PaperClipIcon,
  XIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  OfficeBuildingIcon,
  UserIcon,
  CalendarIcon,
  LocationMarkerIcon,
  ShieldCheckIcon,
  TruckIcon,
  CreditCardIcon,
  StarIcon,
  SparklesIcon,
  TrendingUpIcon
} from '@heroicons/react/outline';
import { doc, getDoc, collection, increment, runTransaction } from 'firebase/firestore';
import { db } from '../firebase-config';
import ImageGallery from '../components/ImageGallery';
import AuthModal from '../components/AuthModal';
import { getQuoteDraft, clearQuoteDraft, updateQuoteDraft } from '../utils/quoteStorage';

const SubmitQuote = () => {
  const { rfqId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isDraftRestored, setIsDraftRestored] = useState(false);
  
  // Quote form state
  const [quoteAmount, setQuoteAmount] = useState('');
  const [formattedAmount, setFormattedAmount] = useState('');
  const [quoteDetails, setQuoteDetails] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [warranty, setWarranty] = useState('');
  const [terms, setTerms] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);

  useEffect(() => {
    const loadRFQDetails = async () => {
      try {
        setLoading(true);
        
        // If we have the RFQ data from navigation state, use it
        if (location.state?.rfq) {
          setRfq(location.state.rfq);
        } else {
          // Otherwise fetch from Firebase
          const rfqDoc = await getDoc(doc(db, 'posts', rfqId));
          if (rfqDoc.exists()) {
            setRfq({ id: rfqDoc.id, ...rfqDoc.data() });
          } else {
            setError('RFQ not found');
          }
        }
      } catch (err) {
        console.error('Error loading RFQ details:', err);
        setError('Failed to load RFQ details');
      } finally {
        setLoading(false);
      }
    };

    loadRFQDetails();
  }, [rfqId, location.state]);

  // Check for and restore quote draft
  useEffect(() => {
    if (rfq && location.state?.restoreDraft) {
      const draft = getQuoteDraft();
      if (draft && draft.rfqId === rfqId) {
        // Restore form data from draft
        setQuoteAmount(draft.quoteAmount || '');
        setFormattedAmount(draft.formattedAmount || '');
        setQuoteDetails(draft.quoteDetails || '');
        setDeliveryTime(draft.deliveryTime || '');
        setWarranty(draft.warranty || '');
        setTerms(draft.terms || '');
        setIsDraftRestored(true);
        
        // Clear the draft since we've restored it
        clearQuoteDraft();
      }
    }
  }, [rfq, rfqId, location.state]);

  // Save draft when form data changes (for unauthenticated users)
  useEffect(() => {
    if (!user && rfq && (quoteAmount || quoteDetails || deliveryTime || warranty || terms)) {
      updateQuoteDraft({
        rfqId: rfq.id,
        quoteAmount,
        formattedAmount,
        quoteDetails,
        deliveryTime,
        warranty,
        terms,
        action: 'submit_quote'
      });
    }
  }, [user, rfq, quoteAmount, formattedAmount, quoteDetails, deliveryTime, warranty, terms]);

  // Format currency input
  const handleAmountChange = (value) => {
    // Remove non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');
    setQuoteAmount(numericValue);
    
    if (numericValue) {
      const formatted = new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(numericValue);
      setFormattedAmount(formatted);
    } else {
      setFormattedAmount('');
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => {
      const maxSize = 5 * 1024 * 1024; // 5MB
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      
      if (file.size > maxSize) {
        setError(`File ${file.name} is too large. Maximum size is 5MB.`);
        return false;
      }
      
      if (!validTypes.includes(file.type)) {
        setError(`File ${file.name} is not a supported type. Please upload images, PDFs, or Word documents.`);
        return false;
      }
      
      return true;
    });
    
    setSelectedFiles(prev => [...prev, ...validFiles].slice(0, 6)); // Max 6 files
  };

  // Remove selected file
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Submit quote
  const handleSubmitQuote = async () => {
    if (!user) {
      // Show authentication modal for unauthenticated users
      setShowAuthModal(true);
      return;
    }

    if (!quoteDetails.trim() && !quoteAmount.trim()) {
      setError('Please enter a price or detailed proposal.');
      return;
    }

    if (quoteAmount && isNaN(quoteAmount)) {
      setError('Please enter a valid price amount.');
      return;
    }

    setSubmitting(true);
    setError('');
    
    try {
      // Create quote data
      const quoteData = {
        postId: rfq.id,
        author: user.uid,
        authorName: user.displayName || user.email,
        authorEmail: user.email,
        offerPrice: quoteAmount || '',
        textContent: quoteDetails || 'Here is my offer',
        deliveryTime: deliveryTime,
        warranty: warranty,
        terms: terms,
        timestamp: Date.now(),
        localId: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        media: [] // For now, we'll skip file upload in web version
      };

      // Submit quote using transaction
      await runTransaction(db, async (transaction) => {
        const postRef = doc(db, 'posts', rfq.id);
        const postDoc = await transaction.get(postRef);

        if (!postDoc.exists()) {
          throw new Error("RFQ does not exist!");
        }

        const replyRef = doc(collection(db, "replies"));
        transaction.set(replyRef, quoteData);
        transaction.update(postRef, { replyCount: increment(1) });
      });

      // Clear any existing draft
      clearQuoteDraft();

      // Success - show success modal
      setShowSuccessModal(true);
      
    } catch (error) {
      console.error('Error submitting quote:', error);
      setError('Failed to submit quote. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAuthSuccess = () => {
    // After successful authentication, the form data is already saved in draft
    // Just close the modal and let the user submit
    setShowAuthModal(false);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date not specified';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading quotation form...</p>
        </div>
      </div>
    );
  }

  if (error || !rfq) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <XIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">RFQ Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The RFQ you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate('/market-feed')}
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            Back to Market Feed
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50">
      {/* Enhanced Header */}
      <div className="bg-white shadow-xl border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <button
                  onClick={() => navigate(`/rfq/${rfq.id}`, { state: { rfq } })}
                  className="mr-6 p-3 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-all duration-300"
                >
                  <ArrowLeftIcon className="h-6 w-6" />
                </button>
                <div>
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center mr-3">
                      <DocumentTextIcon className="h-5 w-5 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900">
                      Professional Quotation
                    </h1>
                  </div>
                  <p className="text-lg text-gray-600">
                    Submit your competitive proposal for this opportunity
                  </p>
                </div>
              </div>
              
              {!user && (
                <div className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 rounded-full text-sm font-medium border border-blue-200">
                  <SparklesIcon className="w-4 h-4 mr-2" />
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></span>
                  Progressive Authentication
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 shadow-sm">
            <div className="flex">
              <XIcon className="h-6 w-6 text-red-400 mr-3 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-red-800">Error</h3>
                <p className="text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - RFQ Details */}
          <div className="lg:col-span-1">
            {/* RFQ Preview Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden sticky top-8">
              <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white">
                <div className="flex items-center mb-4">
                  <OfficeBuildingIcon className="h-6 w-6 mr-3" />
                  <h3 className="text-xl font-bold">Request Details</h3>
                </div>
                <h4 className="text-lg font-semibold mb-2 line-clamp-2">
                  {rfq.textContent || rfq.title || 'RFQ Title'}
                </h4>
                <div className="flex items-center text-orange-100">
                  <UserIcon className="h-4 w-4 mr-2" />
                  <span className="text-sm">{rfq.authorName || rfq.userName || 'Anonymous'}</span>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Key Information */}
                <div className="space-y-4">
                  <div className="flex items-center p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
                    <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mr-4">
                      <CurrencyDollarIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Budget</p>
                      <p className="text-lg font-bold text-gray-900">
                        {rfq.budget ? `₦${rfq.budget.toLocaleString()}` : 'Not specified'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mr-4">
                      <CalendarIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Posted</p>
                      <p className="text-lg font-bold text-gray-900">
                        {formatDate(rfq.timestamp)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl border border-purple-100">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-violet-500 rounded-lg flex items-center justify-center mr-4">
                      <LocationMarkerIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Location</p>
                      <p className="text-lg font-bold text-gray-900">
                        {rfq.location?.name || rfq.location || 'Not specified'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-100">
                    <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-lg flex items-center justify-center mr-4">
                      <TrendingUpIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Category</p>
                      <p className="text-lg font-bold text-gray-900">
                        {(rfq.categories || [rfq.channel || rfq.category || 'General']).join(', ')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* RFQ Description */}
                <div className="border-t border-gray-100 pt-6">
                  <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <DocumentTextIcon className="h-4 w-4 mr-2 text-orange-500" />
                    Description
                  </h5>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {rfq.textContent || 'No detailed description available for this RFQ.'}
                  </p>
                </div>

                {/* RFQ Images */}
                {rfq.media && rfq.media.length > 0 && (
                  <div className="border-t border-gray-100 pt-6">
                    <h5 className="text-sm font-semibold text-gray-700 mb-3">RFQ Images</h5>
                    <div className="rounded-lg overflow-hidden">
                      <ImageGallery images={rfq.media} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Quote Form */}
          <div className="lg:col-span-2">
            {/* Authentication Notice */}
            {!user && (
              <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl shadow-sm">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mr-4">
                    <InformationCircleIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-blue-900 mb-3">
                      Complete Your Professional Quotation
                    </h4>
                    <p className="text-blue-700 mb-4 leading-relaxed">
                      Fill out your comprehensive quote information below. You can complete the entire form and review your submission before signing in to submit your professional proposal.
                    </p>
                    <div className="bg-blue-100 rounded-xl p-4">
                      <p className="text-sm text-blue-800 font-medium">
                        <strong>Progressive Authentication:</strong> Your quotation details will be automatically saved and restored after you sign in.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Draft Restoration Notice */}
            {isDraftRestored && (
              <div className="mb-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl shadow-sm">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mr-4">
                    <CheckCircleIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-green-900 mb-2">
                      Quotation Details Restored
                    </h4>
                    <p className="text-green-700">
                      Your previously entered quotation details have been restored. You can now submit your professional proposal.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Professional Quote Form */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 p-8 text-white">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center mr-4">
                    <DocumentTextIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Professional Quotation</h2>
                    <p className="text-gray-300">Comprehensive proposal for this opportunity</p>
                  </div>
                </div>
              </div>

              <div className="p-8 space-y-8">
                {/* Quote Amount Section */}
                <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-100">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center mr-4">
                      <CurrencyDollarIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Quotation Amount</h3>
                      <p className="text-sm text-gray-600">Enter your competitive pricing</p>
                    </div>
                  </div>
                  
                  <div className="relative">
                    <input
                      type="text"
                      value={quoteAmount}
                      onChange={(e) => handleAmountChange(e.target.value)}
                      placeholder="Enter your quote amount in Naira"
                      className="w-full px-6 py-4 text-lg border-2 border-orange-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 hover:border-orange-300"
                    />
                  </div>
                  
                  {formattedAmount && (
                    <div className="mt-4 p-4 bg-white rounded-xl border border-green-200 shadow-sm">
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                        <span className="text-green-800 font-bold text-lg">{formattedAmount}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Proposal Details Section */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mr-4">
                      <DocumentTextIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Proposal Details</h3>
                      <p className="text-sm text-gray-600">Describe your solution and value proposition</p>
                    </div>
                  </div>
                  
                  <textarea
                    value={quoteDetails}
                    onChange={(e) => setQuoteDetails(e.target.value)}
                    placeholder="Provide a comprehensive description of your solution, approach, methodology, and the value you'll deliver. Include technical specifications, implementation plan, and any unique advantages..."
                    rows={8}
                    className="w-full px-6 py-4 text-gray-700 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-300 resize-none"
                  />
                </div>

                {/* Delivery & Warranty Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mr-4">
                        <TruckIcon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">Delivery Timeline</h3>
                        <p className="text-sm text-gray-600">Expected completion time</p>
                      </div>
                    </div>
                    
                    <input
                      type="text"
                      value={deliveryTime}
                      onChange={(e) => setDeliveryTime(e.target.value)}
                      placeholder="e.g., 2-3 weeks, 30 days, etc."
                      className="w-full px-6 py-4 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300 hover:border-green-300"
                    />
                  </div>

                  <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-2xl p-6 border border-purple-100">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-violet-500 rounded-lg flex items-center justify-center mr-4">
                        <ShieldCheckIcon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">Warranty & Support</h3>
                        <p className="text-sm text-gray-600">Post-delivery guarantees</p>
                      </div>
                    </div>
                    
                    <input
                      type="text"
                      value={warranty}
                      onChange={(e) => setWarranty(e.target.value)}
                      placeholder="e.g., 1 year warranty, 6 months support"
                      className="w-full px-6 py-4 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-300 hover:border-purple-300"
                    />
                  </div>
                </div>

                {/* Additional Terms Section */}
                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl p-6 border border-amber-100">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-lg flex items-center justify-center mr-4">
                      <CreditCardIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Payment Terms & Conditions</h3>
                      <p className="text-sm text-gray-600">Additional terms and conditions</p>
                    </div>
                  </div>
                  
                  <textarea
                    value={terms}
                    onChange={(e) => setTerms(e.target.value)}
                    placeholder="Specify payment terms, milestones, conditions, special requirements, or any other terms relevant to this quotation..."
                    rows={4}
                    className="w-full px-6 py-4 text-gray-700 border-2 border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-300 hover:border-amber-300 resize-none"
                  />
                </div>

                {/* File Attachments Section */}
                <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-6 border border-gray-200">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-gray-500 to-slate-500 rounded-lg flex items-center justify-center mr-4">
                      <PaperClipIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Supporting Documents</h3>
                      <p className="text-sm text-gray-600">Upload relevant files (optional)</p>
                    </div>
                  </div>
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-gray-400 transition-colors duration-300">
                    <PaperClipIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2 font-medium">Upload supporting documents</p>
                    <p className="text-sm text-gray-500 mb-6">Images, PDFs, or Word documents (max 6 files, 5MB each)</p>
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-gray-500 to-slate-500 text-white rounded-xl hover:from-gray-600 hover:to-slate-600 transition-all duration-300 cursor-pointer font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                    >
                      Choose Files
                    </label>
                  </div>
                  
                  {/* Selected Files */}
                  {selectedFiles.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Selected Files:</h4>
                      <div className="space-y-3">
                        {selectedFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                            <span className="text-sm text-gray-700 font-medium">{file.name}</span>
                            <button
                              onClick={() => removeFile(index)}
                              className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                            >
                              <XIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Section */}
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-8 border-t border-gray-100">
                <button
                  onClick={handleSubmitQuote}
                  disabled={submitting}
                  className="w-full flex items-center justify-center px-8 py-6 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-xl shadow-2xl hover:shadow-3xl transform hover:-translate-y-2"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                      Submitting Quotation...
                    </>
                  ) : !user ? (
                    <>
                      <StarIcon className="h-6 w-6 mr-3" />
                      Continue to Submit Quotation
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-6 w-6 mr-3" />
                      Submit Professional Quotation
                    </>
                  )}
                </button>
                
                {/* Additional Info for Unauthenticated Users */}
                {!user && (
                  <div className="mt-6 p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
                    <p className="text-center text-sm text-gray-600 mb-2">
                      <strong>Next Step:</strong> You'll be prompted to sign in or create an account to complete your submission.
                    </p>
                    <p className="text-center text-xs text-gray-500">
                      Your quotation details are automatically saved and will be restored after authentication.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 mb-6">
                <CheckCircleIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Quotation Submitted Successfully!
              </h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Your professional quotation has been submitted and the buyer will be notified. You can track responses in your dashboard.
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate(`/rfq/${rfq.id}`, { state: { rfq } });
                  }}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 font-semibold"
                >
                  View RFQ Details
                </button>
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate('/my-responses');
                  }}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-300 font-semibold"
                >
                  My Responses
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={handleAuthSuccess}
        />
      )}
    </div>
  );
};

export default SubmitQuote; 